import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp } from '@react-navigation/native';
import errorLoggingService, { ErrorLog } from '../../services/errorLoggingService';

// Import icons
import backIcon from '../../assets/icons/back.png';
import trashIcon from '../../assets/icons/trash.png';

import { createLogger } from '../../utils/logger';
const logger = createLogger('ErrorLogs');

interface ErrorLogsScreenProps {
  navigation: NavigationProp<any>;
}

const ErrorLogsScreen: React.FC<ErrorLogsScreenProps> = ({ navigation }) => {
  const [errorLogs, setErrorLogs] = useState<ErrorLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState<string>('');

  useEffect(() => {
    loadErrorLogs();
  }, []);

  const loadErrorLogs = async () => {
    setIsLoading(true);
    try {
      const logs = await errorLoggingService.getStoredErrors();
      const errorSummary = await errorLoggingService.getErrorSummary();
      setErrorLogs(logs);
      setSummary(errorSummary);
    } catch (error) {
      logger.error('Failed to load error logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearLogs = () => {
    Alert.alert(
      'Clear Error Logs',
      'Are you sure you want to clear all error logs? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Clear',
          style: 'destructive',
          onPress: async () => {
            await errorLoggingService.clearStoredErrors();
            await loadErrorLogs();
            Alert.alert('Success', 'Error logs cleared successfully');
          },
        },
      ]
    );
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <Image source={backIcon} style={styles.headerIcon} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Error Logs</Text>
          <View style={styles.placeholder} />
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#29BFC0" />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Image source={backIcon} style={styles.headerIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Error Logs</Text>
        <TouchableOpacity onPress={handleClearLogs}>
          <Image source={trashIcon} style={[styles.headerIcon, styles.deleteIcon]} />
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Developer Only Notice */}
        {__DEV__ && (
          <View style={styles.devNotice}>
            <Text style={styles.devNoticeTitle}>🔧 Developer Mode</Text>
            <Text style={styles.devNoticeText}>
              This screen is only available in development builds
            </Text>
          </View>
        )}

        {/* Summary Card */}
        <View style={styles.summaryCard}>
          <Text style={styles.summaryTitle}>Summary</Text>
          <Text style={styles.summaryText}>{summary}</Text>
        </View>

        {/* Error Logs List */}
        {errorLogs.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyIcon}>✅</Text>
            <Text style={styles.emptyTitle}>No Errors Logged</Text>
            <Text style={styles.emptyText}>
              Your app is running smoothly with no recorded errors.
            </Text>
          </View>
        ) : (
          errorLogs.map((log, index) => (
            <View key={log.id} style={styles.errorCard}>
              <View style={styles.errorHeader}>
                <Text style={styles.errorNumber}>#{errorLogs.length - index}</Text>
                <Text style={styles.errorDate}>{formatDate(log.timestamp)}</Text>
              </View>

              <Text style={styles.errorMessage}>{log.message}</Text>

              {log.additionalInfo && (
                <View style={styles.additionalInfo}>
                  <Text style={styles.infoLabel}>Additional Info:</Text>
                  <Text style={styles.infoText}>
                    {JSON.stringify(log.additionalInfo, null, 2)}
                  </Text>
                </View>
              )}

              {log.stack && (
                <View style={styles.stackContainer}>
                  <Text style={styles.stackLabel}>Stack Trace:</Text>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    style={styles.stackScroll}
                  >
                    <Text style={styles.stackText}>{log.stack}</Text>
                  </ScrollView>
                </View>
              )}

              {log.componentStack && (
                <View style={styles.stackContainer}>
                  <Text style={styles.stackLabel}>Component Stack:</Text>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    style={styles.stackScroll}
                  >
                    <Text style={styles.stackText}>{log.componentStack}</Text>
                  </ScrollView>
                </View>
              )}
            </View>
          ))
        )}

        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingVertical: ms(50),
    marginTop: ms(20),
    backgroundColor: '#F3F6F8',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
  },
  headerIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  deleteIcon: {
    tintColor: '#E85C5C',
  },
  placeholder: {
    width: s(24),
  },
  content: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  devNotice: {
    backgroundColor: '#FFF3CD',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(15),
    borderWidth: ms(1),
    borderColor: '#FFC107',
  },
  devNoticeTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#856404',
    marginBottom: ms(5),
  },
  devNoticeText: {
    fontSize: ms(14, 0.3),
    color: '#856404',
  },
  summaryCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  summaryTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(10),
  },
  summaryText: {
    fontSize: ms(14, 0.3),
    color: '#666',
    fontFamily: 'monospace',
    lineHeight: ms(22, 0.3),
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: ms(60),
  },
  emptyIcon: {
    fontSize: ms(60, 0.3),
    marginBottom: ms(20),
  },
  emptyTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(10),
  },
  emptyText: {
    fontSize: ms(16, 0.3),
    color: '#666',
    textAlign: 'center',
    paddingHorizontal: ms(40),
  },
  errorCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(15),
    marginBottom: ms(15),
    borderWidth: ms(1),
    borderColor: '#E85C5C',
    borderLeftWidth: ms(4),
  },
  errorHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  errorNumber: {
    fontSize: ms(12, 0.3),
    fontWeight: 'bold',
    color: '#E85C5C',
  },
  errorDate: {
    fontSize: ms(12, 0.3),
    color: '#666',
  },
  errorMessage: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#333',
    marginBottom: ms(10),
  },
  additionalInfo: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(8),
    padding: ms(10),
    marginBottom: ms(10),
  },
  infoLabel: {
    fontSize: ms(12, 0.3),
    fontWeight: 'bold',
    color: '#666',
    marginBottom: ms(5),
  },
  infoText: {
    fontSize: ms(12, 0.3),
    color: '#333',
    fontFamily: 'monospace',
  },
  stackContainer: {
    marginTop: ms(10),
  },
  stackLabel: {
    fontSize: ms(12, 0.3),
    fontWeight: 'bold',
    color: '#666',
    marginBottom: ms(5),
  },
  stackScroll: {
    maxHeight: s(100),
  },
  stackText: {
    fontSize: ms(11, 0.3),
    color: '#E85C5C',
    fontFamily: 'monospace',
    lineHeight: ms(16, 0.3),
  },
  bottomPadding: {
    height: s(40),
  },
});

export default ErrorLogsScreen;
